import { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import isEqual from 'deep-equal';
import { withRouter } from 'next/router';

import { trackResultPage } from '@/lib/tracking/tracking';
import * as resultActions from '@/ducks/results/resultsActions';

import { TopBarMap } from '../topBarMap/TopBarMap';
import { TopBarMapMobile } from '../topBarMap/TopBarMapMobile';
import { ResultListMap } from '../../components/resultListMap/ResultListMap';
import { ResultFilterMap } from '../../components/resultFilterMap/ResultFilterMap';
import { FhhFullScreenMap } from '../../components/fullScreenMap/FullScreenMap';

import 'shared/map/map.css';

export class ResultMap extends Component {
    constructor(props) {
        super(props);
        this.toggleFilterState = this.toggleFilterState.bind(this);
        this.hoverIdCallback = this.hoverIdCallback.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.state = {
            isMobile: !!props.deviceType.isProbablyMobile,
            filtersOpen: false,
            selectedAdId: null,
            windowSize: [],
        };
    }

    componentDidMount() {
        if (this.state.windowSize.length > 0) {
            let rowCoefficient = 1;
            if (!this.state.isMobile) {
                rowCoefficient = 6;
            }
            const rows = Math.floor(this.state.windowSize[1] / 200) + rowCoefficient;
            const columns = Math.floor(this.state.windowSize[0] / 200) + 4;
            this.props.updateRowsAndColumnsForMap(rows, columns);
        }
        this.populateResultsIfEmpty();
        this.props.setNumberOfFusAdsMapDefault(this.props.filters);
        if (!this.isFalseyOrEmpty(this.props.router?.query)) {
            this.props.updateFilters(this.props.router.query);
        }
        window.addEventListener('resize', this.handleResize);
        this.handleResize();
    }

    componentDidUpdate(prevProps) {
        const filtersChanged = !isEqual(this.props.filters, prevProps.filters);

        if (filtersChanged) {
            this.props.router?.replace({ pathname: this.props.router.pathname, query: this.props.filters }, undefined, { shallow: true });
        }

        // On a page load, the stats object is empty. This object is needed for tracking, so must wait until
        // next update when stats is set before tracking. After that, track on every filter change.
        if (!isEqual(this.props.data.stats, prevProps.data.stats) || filtersChanged) {
            if (this.props.data.stats?.numResults !== undefined) {
                // Pulse tracker id: FHH_pv08 (see tracking section in README.md)
                trackResultPage('Map', this.props);
            }
        }
    }

    toggleFilterState() {
        this.setState({
            filtersOpen: !this.state.filtersOpen,
        });
    }

    handleResize() {
        if (typeof window !== 'undefined') {
            // Set window width/height to state
            this.setState({
                windowSize: [window.innerWidth, window.innerHeight],
            });
        }
    }

    hoverIdCallback(finnId, currentState) {
        if (currentState === 'start') {
            this.setState({
                selectedAdId: finnId,
            });
        } else if (currentState === 'end') {
            this.setState({
                selectedAdId: null,
            });
        }
    }

    populateResultsIfEmpty() {
        const query = this.props.router?.query || {};

        if (this.props.data.resultItems === null) {
            this.props.populateResults(Object.assign({}, this.props.filters, query));
        }
    }

    isFalseyOrEmpty(value) {
        return !value || (Object.prototype.toString.call(value) === '[object Object]' && JSON.stringify(value) === '{}');
    }

    render() {
        const { filterData, filters, data, loginId, unleash } = this.props;
        const filterResults = this.props.filterResults.bind('api');

        return (
            <>
                <div className="map-result-list px-8 hidden md:block bg-white">
                    <TopBarMap
                        filtersOpen={this.state.filtersOpen}
                        toggleFilterState={this.toggleFilterState}
                        numResults={this.props.data?.stats?.numResults}
                    />
                    <ResultListMap
                        results={data.resultItems}
                        stats={data.stats}
                        filters={filters}
                        onFilterChanged={filterResults}
                        numberOfPreloadedImages={6}
                        isMobile={this.state.isMobile}
                        loginId={loginId}
                        unleash={unleash}
                    />
                </div>
                <TopBarMapMobile
                    filtersOpen={this.state.filtersOpen}
                    toggleFilterState={this.toggleFilterState}
                    numResults={this.props.data?.stats?.numResults}
                />
                <FhhFullScreenMap
                    filters={this.props.filters}
                    data={this.props.data}
                    populateMapResults={this.props.populateMapResults}
                    onFilterChanged={this.props.filterResults}
                    selectedAdId={this.state.selectedAdId ?? this.props.selectedAd?.adId?.toString()}
                    populateMapObject={this.props.populateMapObject}
                    selectedAdData={this.props.selectedAd}
                />
                <ResultFilterMap
                    filtersOpen={this.state.filtersOpen}
                    filters={filters}
                    filterResults={filterResults}
                    filterData={filterData}
                />
            </>
        );
    }
}

const mapStateToProps = (state) => ({
    data: state.results.data,
    filters: state.results.filters,
    selectedAd: state.results.selectedAd,
    filterData: state.results.filterData,
});

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            populateResults: resultActions.populateResults,
            updateFilters: resultActions.updateFilters,
            filterResults: resultActions.filterResults,
            populateMapResults: resultActions.populateMapResults,
            populateMapObject: resultActions.populateMapObject,
            setNumberOfFusAdsMapDefault: resultActions.setNumberOfFusAdsMapDefault,
            updateRowsAndColumnsForMap: resultActions.updateRowsAndColumnsForMap,
        },
        dispatch,
    );

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ResultMap));

ResultMap.propTypes = {
    updateFilters: PropTypes.func.isRequired,
    data: PropTypes.object.isRequired,
    filterResults: PropTypes.func.isRequired,
    populateResults: PropTypes.func.isRequired,
    deviceType: PropTypes.object.isRequired,
    filters: PropTypes.object.isRequired,
    filterData: PropTypes.object.isRequired,
    loginId: PropTypes.number,
    router: PropTypes.object,
    populateMapResults: PropTypes.func.isRequired,
    populateMapObject: PropTypes.func.isRequired,
    setNumberOfFusAdsMapDefault: PropTypes.func.isRequired,
    updateRowsAndColumnsForMap: PropTypes.func.isRequired,
    selectedAd: PropTypes.object,
    unleash: PropTypes.object.isRequired,
};
